import type { TFunction } from "i18next";
import type { MonthChange } from "./usagePace";

/**
 * The full comparison sentence for a month-over-month change: "On course for 12% more than
 * September" against a projection, or "12% less than September" against an actual total. Shared by
 * the hero's comparison tile and each vendor card's chip, so both say the same thing.
 *
 * @param t - The translation function.
 * @param change - The change to describe.
 * @param basis - Whether the current figure is a projection (current month) or an actual total.
 * @param month - The previous month's long name.
 * @returns The translated sentence.
 */
export function comparisonSentence(t: TFunction, change: MonthChange, basis: "projected" | "actual", month: string): string {
  const percent = change.percent;
  if (basis === "projected") {
    if (change.direction === "up") {
      return t("consumption.comparison.projectedUp", { percent, month });
    }
    return change.direction === "down"
      ? t("consumption.comparison.projectedDown", { percent, month })
      : t("consumption.comparison.projectedSame", { month });
  }
  if (change.direction === "up") {
    return t("consumption.comparison.actualUp", { percent, month });
  }
  return change.direction === "down"
    ? t("consumption.comparison.actualDown", { percent, month })
    : t("consumption.comparison.actualSame", { month });
}

/**
 * The arrow that marks a change's direction, or null for no meaningful change. Always rendered
 * beside words that state the direction, so colour and the glyph are never the only signal.
 *
 * @param change - The change to mark.
 * @returns "↑", "↓" or null.
 */
export function comparisonArrow(change: MonthChange): string | null {
  if (change.direction === "up") {
    return "↑";
  }
  return change.direction === "down" ? "↓" : null;
}
